import { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useBoolean } from 'react-use'
import cx from 'classnames'
import { toBlob, toJpeg, toPng } from 'html-to-image'
import toast, { Toaster } from 'react-hot-toast'
import { useTranslations } from 'next-intl'
import { GetStaticProps } from 'next'

import Header from '../components/layouts/header'
import Switch from '../components/switch'
import Upload from '../svg/upload'
import { download } from '../utils/blob'
import Copy from '../svg/copy'
import { Button } from '../components/ui/button'
import { Seo } from '@/components/seo'
import { useLocalizedUrl } from '@/hooks/useLocalizedUrl'
import PasteImageButton from '@/components/paste-image-button'

type Color = 'orange' | 'green' | 'blue' | 'purple' | 'red'

const gradients: Record<Color, string> = {
  orange: 'from-orange-500 via-amber-500 to-yellow-500',
  green: 'from-green-500 via-emerald-500 to-teal-500',
  blue: 'from-sky-500 via-cyan-500 to-blue-500',
  purple: 'from-purple-500 via-violet-500 to-indigo-500',
  red: 'from-red-500 via-pink-500 to-rose-500'
}

interface Setting {
  title?: string;
  setTitle: (title: string) => void;

  author?: string;
  setAuthor: (author: string) => void;

  color: Color;
  setColor: (color: Color) => void;

  paddingX: string;
  setPaddingX: (n: string) => void;

  paddingY: string;
  setPaddingY: (n: string) => void;

  enableRounded: boolean;
  toggleRounded: () => void;

  imageRef: RefObject<HTMLDivElement>
}

function ImageShareSetting ({
  title,
  setTitle,
  author,
  setAuthor,
  color,
  setColor,
  enableRounded,
  toggleRounded,
  paddingX,
  setPaddingX,
  paddingY,
  setPaddingY,
  imageRef: ref
}: Setting) {
  const t = useTranslations('ImageShare')

  return (
    <div className="relative inline-flex gap-6 p-6 mb-6 text-sm text-center text-gray-700 -translate-x-1/2 bg-white border border-gray-300 shadow-sm left-1/2 rounded-xl">
      <div>
        <input
          type="text"
          className="mb-2 border-gray-300 shadow-none app-input w-[280px]"
          placeholder={t('inputTitle')}
          onChange={(e) => setTitle(e.target.value)}
          value={title}
          spellCheck={false}
        />
        <input
          type="text"
          className="border-gray-300 shadow-none app-input"
          placeholder={t('inputAuthor')}
          onChange={(e) => setAuthor(e.target.value)}
          value={author}
          spellCheck={false}
        />
      </div>
      <div>
        <div className="mb-2">{t('paddingX')}</div>
        <input
          type="number"
          className="app-input"
          value={paddingX}
          max={300}
          min={0}
          onChange={(e) => {
            setPaddingX(e.target.value)
          }}
        />
      </div>
      <div>
        <div className="mb-2">{t('paddingY')}</div>
        <input
          type="number"
          className="app-input"
          value={paddingY}
          max={300}
          min={0}
          onChange={(e) => {
            setPaddingY(e.target.value)
          }}
        />
      </div>
      <div>
        <div className="mb-2">{t('background')}</div>
        <select
          className="mb-3 bg-gray-100 border-none app-input focus:border-gray-300 focus:ring-gray-300"
          value={color}
          onChange={e => setColor(e.target.value as Color)}
        >
          {
            Object.keys(gradients).map((c) => (
              <option key={c}>
                {c}
              </option>
            ))
          }
        </select>
      </div>
      <div>
        <div className="mb-2">{t('rounded')}</div>
        <Switch checked={enableRounded} onChange={toggleRounded} />
      </div>
      <div>
        <div className="mb-2 text-center">{t('export')}</div>
        <button className="app-button" onClick={() => {
          if (ref.current) {
            toPng(ref.current).then(url => {
              download(url, 'devtool-image-share-download.png')
            })
          }
        }}>
          PNG
        </button>
        <button className="ml-2 app-button" onClick={() => {
          if (ref.current) {
            toJpeg(ref.current).then(url => {
              download(url, 'devtool-image-share-download.jpg')
            })
          }
        }}>
          JPG
        </button>
      </div>
      <div>
        <div className="mb-2">{t('copy')}</div>
        <Button
          onClick={async () => {
            if (!ref.current) {
              toast.error(t('errorNoImage'))
              return
            }
            toast.loading(t('copyingInProgress'), { id: 'copy' })
            const blob = await toBlob(ref.current as HTMLDivElement)
            if (blob) {
              navigator.clipboard.write([new ClipboardItem({
                'image/png': blob
              })])
              toast.success(t('copySuccess'), { id: 'copy' })
            }
          }}
        >
          <Copy className="w-5 h-5" />
          <span className="ml-1">{t('copyToClipboard')}</span>
        </Button>
      </div>
    </div>
  )
}

function ImageShare() {
  const t = useTranslations('ImageShare')
  const [file, setFile] = useState<File | null>(null)
  const [color, setColor] = useState<Color>('orange')
  const [enableRounded, toggleRounded] = useBoolean(true)
  const [title, setTitle] = useState('Example')
  const [author, setAuthor] = useState('By devtool.tech')
  const [paddingX, setPaddingX] = useState('32')
  const [paddingY, setPaddingY] = useState('32')

  const ref = useRef<HTMLDivElement>(null)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFile(acceptedFiles[0])
  }, [file])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
  })

  const originImage = useMemo(() => {
    if (file) {
      return URL.createObjectURL(file)
    }
  }, [file])

  const handlePaste = useCallback((file: File) => {
    setFile(file)
  }, [])

  return (
    <div className="container">
      <Seo
        title={t('pageTitle')}
        description={t('metaDescription')}
        path="/image-share"
        thumbnail="https://devtool.tech/thumbnail/image-share.png"
      />
      <Header title={t('headerTitle')} />
      <Toaster />
      <ImageShareSetting {
        ...{
          title,
          setTitle,
          author,
          setAuthor,
          enableRounded, 
          toggleRounded,
          paddingX,
          setPaddingX,
          paddingY,
          setPaddingY,
          color,
          setColor,
          imageRef: ref
        }
      }/>
      <div {...getRootProps()} className={cx('grid p-4 mb-4 place-items-center', { 'app-upload': !file })}>
        <input {...getInputProps()} />
        {
          file ? (
            <div style={{ padding: `${paddingY}px ${paddingX}px` }} className={cx('text-center bg-gradient-to-bl text-white', gradients[color])} ref={ref}>
              <div className={cx('mb-2 text-2xl', !title && 'hidden')}>
                {title}
              </div>
              <div className={cx('mb-2 opacity-80', !author && 'hidden')}>
                {author}
              </div>
              <img
                src={originImage}
                className={cx(enableRounded && 'rounded-2xl')}
                style={{
                  boxShadow: 'rgb(0 0 0 / 50%) 0px 2em 4em 0px'
                }}
              />
            </div>
          ) : (
            <>
              <Upload className="text-orange-500 fill-current" />
              <p className="my-1 italic text-gray-600">{t('uploadImage')}</p>
              <p className="my-1 text-gray-700">
                <PasteImageButton onPaste={handlePaste} />
                <span>{t('dragAndDrop')}</span>
              </p>
              <div className="text-gray-700">
                {t('uploadInstructions')}
              </div>
            </>
          )
        }
      </div>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      messages: (await import(`../messages/${locale}.json`)).default
    }
  }
}

export default ImageShare
